import { useRouter } from 'expo-router';
import React, { useState } from 'react';
import { ScrollView, Pressable, View } from 'react-native';
import { useTranslation } from 'react-i18next';

import { TeamCrest } from '@/components/matches/TeamCrest';
import { AppText } from '@/components/ui/AppText';
import { BackHeader } from '@/components/ui/BackHeader';
import { Card } from '@/components/ui/Card';
import { EmptyState } from '@/components/ui/EmptyState';
import { ErrorState } from '@/components/ui/ErrorState';
import { Screen } from '@/components/ui/Screen';
import { Skeleton } from '@/components/ui/Skeleton';
import { TextField } from '@/components/ui/TextField';
import { isSupabaseConfigured } from '@/config';
import { useSearch } from '@/features/search/hooks';

function ResultRow({
  title,
  subtitle,
  imageUrl,
  onPress,
}: {
  title: string;
  subtitle?: string;
  imageUrl: string | null;
  onPress: () => void;
}) {
  return (
    <Pressable
      accessibilityRole="button"
      onPress={onPress}
      className="flex-row items-center gap-3 px-4 py-3 active:bg-surface-2/60"
    >
      <TeamCrest uri={imageUrl} name={title} size="md" />
      <View className="flex-1">
        <AppText variant="caption" weight="semibold" className="text-ink" numberOfLines={1}>
          {title}
        </AppText>
        {subtitle ? (
          <AppText variant="micro" numberOfLines={1}>
            {subtitle}
          </AppText>
        ) : null}
      </View>
    </Pressable>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <View className="mb-4 px-4">
      <AppText variant="micro" weight="bold" className="mb-2 uppercase text-muted">
        {title}
      </AppText>
      <Card className="overflow-hidden">{children}</Card>
    </View>
  );
}

export default function SearchScreen() {
  const router = useRouter();
  const { t } = useTranslation();
  const [query, setQuery] = useState('');
  const search = useSearch(query);

  const results = search.data;
  const hasResults =
    results &&
    (results.players.length > 0 || results.teams.length > 0 || results.competitions.length > 0);

  return (
    <Screen>
      <BackHeader title={t('tabs.search')} />
      <View className="px-4 pb-4">
        <TextField
          placeholder={t('search.placeholder')}
          value={query}
          onChangeText={setQuery}
          autoCorrect={false}
          accessibilityLabel={t('search.placeholder')}
        />
      </View>

      {!isSupabaseConfigured ? (
        <EmptyState
          icon="settings-outline"
          title={t('common.notConfiguredTitle')}
          body={t('common.notConfiguredBody')}
        />
      ) : !search.active ? (
        <EmptyState
          icon="search-outline"
          title={t('search.startTypingTitle')}
          body={t('search.startTypingBody')}
        />
      ) : search.isPending ? (
        <View className="gap-3 px-4">
          {[0, 1, 2].map((index) => (
            <Skeleton key={index} className="h-14 w-full" />
          ))}
        </View>
      ) : search.isError ? (
        <ErrorState error={search.error} onRetry={() => search.refetch()} />
      ) : !hasResults ? (
        <EmptyState
          icon="search-outline"
          title={t('search.noResultsTitle', { query: search.debouncedQuery })}
          body={t('search.noResultsBody')}
        />
      ) : (
        <ScrollView contentContainerStyle={{ paddingBottom: 24 }}>
          {results.players.length > 0 ? (
            <Section title={t('search.players')}>
              {results.players.map((player, index) => (
                <View key={player.id} className={index > 0 ? 'border-t border-line' : ''}>
                  <ResultRow
                    title={player.known_as ?? player.name}
                    subtitle={player.team?.name ?? undefined}
                    imageUrl={player.photo_url}
                    onPress={() => router.push(`/player/${player.id}`)}
                  />
                </View>
              ))}
            </Section>
          ) : null}
          {results.teams.length > 0 ? (
            <Section title={t('search.teams')}>
              {results.teams.map((team, index) => (
                <View key={team.id} className={index > 0 ? 'border-t border-line' : ''}>
                  <ResultRow
                    title={team.name}
                    subtitle={team.country ?? undefined}
                    imageUrl={team.logo_url}
                    onPress={() => router.push(`/team/${team.id}`)}
                  />
                </View>
              ))}
            </Section>
          ) : null}
          {results.competitions.length > 0 ? (
            <Section title={t('search.competitions')}>
              {results.competitions.map((competition, index) => (
                <View key={competition.id} className={index > 0 ? 'border-t border-line' : ''}>
                  <ResultRow
                    title={competition.name}
                    subtitle={competition.country ?? undefined}
                    imageUrl={competition.logo_url}
                    onPress={() => router.push(`/competition/${competition.id}`)}
                  />
                </View>
              ))}
            </Section>
          ) : null}
        </ScrollView>
      )}
    </Screen>
  );
}
